import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import api from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Building2, CheckCircle2, FileSignature, ShieldCheck } from "lucide-react";

/**
 * PartnerAgreementPage (finbpm-flow-002) — the e-sign FinBPM participation
 * agreement. Eligibility is Step-1-gated server-side; this page renders the
 * versioned terms, the member's read-only identity, the amount election
 * (minimum $250,000), the compliance statements, and the typed-signature
 * execution. This surface may name FinBPM (recorded naming exception — the
 * member is contracting with FinBPM).
 */
const PartnerAgreementPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [amount, setAmount] = useState("");
  const [typedName, setTypedName] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fundingMethod, setFundingMethod] = useState("");   // funding-instructions-001
  const [fundingAsset, setFundingAsset] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewUrlRef = useRef(null);
  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);
  useEffect(() => () => {
    // Unmount cleanup: the last object URL would otherwise leak.
    if (previewUrlRef.current) window.URL.revokeObjectURL(previewUrlRef.current);
  }, []);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [idAddress, setIdAddress] = useState("");
  const [idNationality, setIdNationality] = useState("");
  const [savingIdentity, setSavingIdentity] = useState(false);
  // CAPMARKETS-052: contracting-party election ("Investing as").
  const [signingType, setSigningType] = useState("individual");
  const [entityName, setEntityName] = useState("");
  const [signerTitle, setSignerTitle] = useState("");
  const [savingEntity, setSavingEntity] = useState(false);
  // In-progress edits must survive unrelated reloads (saving the identity
  // card also calls load()) — the prefill effect only syncs while clean.
  const signingDirtyRef = useRef(false);

  const load = async () => {
    setLoadError(null);
    try {
      const r = await api.get("/finbpm/agreement/me");
      setData(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setLoadError((typeof detail === "string" && detail) || "Could not load the agreement. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Keyed on updated_at (a stable primitive — the object identity changes
    // on every load()) and skipped while the member has unsaved edits, so a
    // reload can never silently clobber an in-progress election
    // (CAPMARKETS-052 review fix).
    const se = data?.signing_entity;
    if (!se || signingDirtyRef.current) return;
    setSigningType(se.type || "individual");
    setEntityName(se.entity_name || "");
    setSignerTitle(se.signer_title || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.signing_entity?.updated_at]);

  // forge-20260611-011 phase A: the member reads the REAL pre-filled
  // document. Blob-fetched through the authenticated client (a bare iframe
  // src would carry no Bearer token).
  const loadPreview = async (amountValue) => {
    setPreviewLoading(true);
    try {
      const params = {};
      const amt = parseFloat(amountValue);
      if (!Number.isNaN(amt) && amt > 0) params.amount = amt;
      const r = await api.get("/finbpm/agreement/preview.pdf", {
        params, responseType: "blob",
      });
      setPreviewUrl((prev) => {
        if (prev) window.URL.revokeObjectURL(prev);
        return window.URL.createObjectURL(r.data);
      });
    } catch {
      toast.error("Could not load the agreement preview");
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    // Preview renders whenever the member can (re-)sign: no agreement yet,
    // or the prior one was rejected / voided (CAPMARKETS-052 review fix).
    const a = data?.agreement;
    const canSign = !a || a.partner_rejected_at || a.superseded_at;
    if (data?.eligible && canSign) loadPreview(amount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.eligible, data?.agreement?.partner_rejected_at, data?.agreement?.superseded_at]);

  const saveIdentityFields = async () => {
    setSavingIdentity(true);
    try {
      await api.post("/finbpm/agreement/identity-fields", {
        residential_address: idAddress || null,
        nationality: idNationality || null,
      });
      toast.success("Details saved — preview updated");
      await load();
      await loadPreview(amount);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Could not save your details");
    } finally {
      setSavingIdentity(false);
    }
  };

  const saveSigningEntity = async () => {
    if (signingType === "entity" && !entityName.trim()) {
      toast.error("Enter the entity's exact legal name");
      return;
    }
    setSavingEntity(true);
    try {
      await api.put("/signing-entity/me", {
        type: signingType,
        entity_name: signingType === "entity" ? entityName : null,
        signer_title: signingType === "entity" ? (signerTitle || null) : null,
      });
      signingDirtyRef.current = false;
      toast.success("Saved — the agreement preview now names this party");
      await load();
      await loadPreview(amount);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || "Could not save your election");
    } finally {
      setSavingEntity(false);
    }
  };

  const downloadSignedPdf = async () => {
    try {
      // doc-access-001: dedicated member endpoint serving a watermarked
      // PERSONAL COPY of the member's own executed agreement.
      const r = await api.get("/finbpm/agreement/document.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "agreement-personal-copy.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download your signed agreement");
    }
  };

  const sign = async () => {
    if (fundingMethod === "crypto" && !fundingAsset) {
      toast.error("Choose the crypto asset & network, or clear the funding selection.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/finbpm/agreement/sign", {
        amount_elected: Number(amount),
        typed_name: typedName,
        acknowledged,
        funding_method: fundingMethod || null,
        funding_asset: fundingMethod === "crypto" ? (fundingAsset || null) : null,
      });
      toast.success("Agreement signed — it has been delivered to the partner for review.");
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === "string" && detail) || detail?.message || "Could not sign the agreement");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Partner Agreement" subtitle="Loading...">
        <div className="animate-pulse h-32 bg-slate-100 rounded" />
      </DashboardLayout>
    );
  }
  if (loadError || !data) {
    return (
      <DashboardLayout title="Partner Agreement" subtitle="">
        <Card className="bg-white border-slate-200" data-testid="agreement-load-error">
          <CardContent className="p-6 flex items-center gap-3">
            <AlertTriangle className="text-amber-600 shrink-0" size={20} />
            <p className="text-sm text-slate-800 flex-1">{loadError || "Could not load the agreement."}</p>
            <Button variant="outline" onClick={load} className="border-slate-300">Retry</Button>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  // --- Already executed / in progress: status view ---
  // Rejected and voided (superseded) agreements fall through to the FULL
  // flow below instead: those are exactly the states where the member may
  // correct their details — including the signing-party election — and
  // execute anew, so they need the election card, the live preview, and
  // the sign form, not a dead-end status card (CAPMARKETS-052 review fix).
  if (data.agreement && !data.agreement.partner_rejected_at && !data.agreement.superseded_at) {
    const a = data.agreement;
    return (
      <DashboardLayout title="Partner Agreement" subtitle="Your participation agreement">
        <Card className="bg-white border-slate-200" data-testid="agreement-status-card">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <FileSignature size={20} className="text-slate-700" /> Agreement status
            </CardTitle>
            <CardDescription className="text-slate-600">
              Terms version {a.terms_version}
              {a.pilot_label && <span className="block mt-1 text-amber-700">{a.pilot_label}</span>}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Badge className="bg-blue-50 text-blue-800 border-blue-200" data-testid="agreement-status">
              {a.status}
            </Badge>
            <p className="text-sm text-slate-700">
              Elected participation amount:{" "}
              <span className="font-semibold">${Number(a.amount_elected).toLocaleString()}</span>
            </p>
            {a.investor_signed_at && (
              <p className="text-sm text-slate-600 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-600" />
                You signed on {new Date(a.investor_signed_at).toLocaleString()}
              </p>
            )}
            {a.partner_signed_at && (
              <p className="text-sm text-slate-600 flex items-center gap-2">
                <CheckCircle2 size={16} className="text-emerald-600" />
                FinBPM countersigned on {new Date(a.partner_signed_at).toLocaleString()}
              </p>
            )}
            {a.signed_pdf_file_id && (
              <Button variant="outline" className="border-slate-300"
                onClick={downloadSignedPdf}
                data-testid="download-signed-agreement">
                Download your copy (PDF)
              </Button>
            )}
            <p className="text-xs text-slate-500">{data.compliance.no_guarantee}</p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  // --- Not eligible yet ---
  if (!data.eligible) {
    return (
      <DashboardLayout title="Partner Agreement" subtitle="Complete onboarding first">
        <Card className="bg-white border-slate-200" data-testid="agreement-not-eligible">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-600" /> A few steps remain
            </CardTitle>
            <CardDescription className="text-slate-600">
              The partner agreement opens once your onboarding checklist is complete.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {data.missing_items.map((m) => (
                <li key={m.key} className="text-sm text-slate-700">• {m.label}</li>
              ))}
            </ul>
            <Link to="/command-center">
              <Button className="mt-4 bg-slate-900 text-white hover:bg-slate-800">
                Open your checklist <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  // --- Eligible: terms + sign form (fresh, rejected, or voided) ---
  const prior = data.agreement;
  return (
    <DashboardLayout title="Partner Agreement" subtitle="FinBPM participation agreement">
      {prior?.partner_rejected_at && (
        <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="agreement-rejected-panel">
          <p className="text-sm font-semibold text-amber-800">
            The partner needs corrections or more information
          </p>
          {prior.partner_rejection_reason_client && (
            <p className="text-sm text-amber-700 mt-1">{prior.partner_rejection_reason_client}</p>
          )}
          <p className="text-xs text-amber-700 mt-2">
            Review your details below — including who the agreement should name
            as the participating party — then execute a new agreement.
          </p>
        </div>
      )}
      {prior?.superseded_at && !prior?.partner_rejected_at && (
        <div className="mb-6 rounded-md border border-slate-300 bg-slate-50 p-3" data-testid="agreement-voided-panel">
          <p className="text-sm font-semibold text-slate-800">
            Your previous agreement was voided
          </p>
          <p className="text-xs text-slate-600 mt-2">
            Review your details below — including who the agreement should name
            as the participating party — then execute a new agreement. Your
            contract number stays the same.
          </p>
        </div>
      )}
      <Card className="mb-6 bg-white border-slate-200" data-testid="agreement-identity-card">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <ShieldCheck size={20} className="text-slate-700" /> Your verified details
          </CardTitle>
          <CardDescription className="text-slate-600">
            These details come from your verified profile and will be part of the executed agreement.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-700">
          <p>Legal name: <span className="font-semibold">{data.identity.full_name}</span></p>
          <p>Email: <span className="font-semibold">{data.identity.email}</span></p>
          <p>Phone: <span className="font-semibold">{data.identity.phone || "—"}</span></p>
          <p>KYC: <span className="font-semibold">{data.identity.kyc_status}</span></p>
          <p>Accredited status: <span className="font-semibold">{data.identity.accredited_status}</span></p>
          <p>File: <span className="font-semibold">{data.identity.vc_number || "—"}</span></p>
        </CardContent>
      </Card>

      <Card className="mb-6 bg-white border-slate-200" data-testid="signing-entity-card">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Building2 size={20} className="text-slate-700" /> Investing as
          </CardTitle>
          <CardDescription className="text-slate-600">
            Who should the agreement name as the participating party? You remain the
            verified signatory either way — your typed signature stays your own legal name.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Button
              variant={signingType === "individual" ? "default" : "outline"}
              className={signingType === "individual" ? "bg-slate-900 text-white hover:bg-slate-800" : ""}
              onClick={() => { signingDirtyRef.current = true; setSigningType("individual"); }}
              data-testid="signing-type-individual"
            >
              Myself ({data.identity.full_name})
            </Button>
            <Button
              variant={signingType === "entity" ? "default" : "outline"}
              className={signingType === "entity" ? "bg-slate-900 text-white hover:bg-slate-800" : ""}
              onClick={() => { signingDirtyRef.current = true; setSigningType("entity"); }}
              data-testid="signing-type-entity"
            >
              An entity (LLC, trust, corporation)
            </Button>
          </div>
          {signingType === "entity" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-700">Entity legal name</Label>
                <Input
                  value={entityName}
                  onChange={(e) => { signingDirtyRef.current = true; setEntityName(e.target.value); }}
                  placeholder="e.g. JC Capital Funding LLC"
                  data-testid="signing-entity-name"
                />
              </div>
              <div>
                <Label className="text-slate-700">Your title (optional)</Label>
                <Input
                  value={signerTitle}
                  onChange={(e) => { signingDirtyRef.current = true; setSignerTitle(e.target.value); }}
                  placeholder="e.g. Managing Member"
                  data-testid="signing-entity-title"
                />
              </div>
            </div>
          )}
          <Button
            onClick={saveSigningEntity}
            disabled={savingEntity || (signingType === "entity" && !entityName.trim())}
            className="bg-blue-700 hover:bg-blue-800 text-white"
            data-testid="signing-entity-save"
          >
            {savingEntity ? "Saving…" : "Save & update preview"}
          </Button>
        </CardContent>
      </Card>

      <Card className="mb-6 bg-white border-slate-200" data-testid="agreement-terms-card">
        <CardHeader>
          <CardTitle className="text-slate-900">{data.terms.title}</CardTitle>
          <CardDescription className="text-slate-600">Terms version {data.terms.version}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-md p-4 space-y-4" data-testid="agreement-terms-scroll">
            {data.terms.sections.map((s) => (
              <div key={s.heading}>
                <h3 className="text-sm font-semibold text-slate-900">{s.heading}</h3>
                {s.body.map((p, i) => (
                  <p key={`${s.heading}-${i}`} className="text-xs text-slate-600 mt-1 leading-relaxed">{p}</p>
                ))}
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 text-xs text-slate-600">
            <p>{data.compliance.echosense_role}</p>
            <p>{data.compliance.finbpm_role}</p>
            <p className="font-medium text-amber-700" data-testid="incoming-charge-disclosure">
              {data.compliance.incoming_charge}
            </p>
            <p className="font-medium text-slate-700">{data.compliance.no_guarantee}</p>
          </div>
        </CardContent>
      </Card>

      {data.verified_identity &&
        (!data.verified_identity.residential_address || !data.verified_identity.nationality) && (
        <Card className="mb-6 bg-white border-amber-200" data-testid="identity-fields-card">
          <CardHeader>
            <CardTitle className="text-slate-900">Complete your details</CardTitle>
            <CardDescription className="text-slate-600">
              These appear on the agreement. Fields verified by our KYC provider
              fill automatically — only what we could not verify is asked here.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!data.verified_identity.residential_address && (
              <div>
                <Label className="text-slate-900">Residential address</Label>
                <Input value={idAddress} onChange={(e) => setIdAddress(e.target.value)}
                  placeholder="Street, City, State/Province, Postal code, Country"
                  className="mt-1 bg-white text-slate-900 border-slate-300"
                  data-testid="identity-address" />
              </div>
            )}
            {!data.verified_identity.nationality && (
              <div>
                <Label className="text-slate-900">Nationality</Label>
                <Input value={idNationality} onChange={(e) => setIdNationality(e.target.value)}
                  placeholder="e.g. USA" className="mt-1 bg-white text-slate-900 border-slate-300"
                  data-testid="identity-nationality" />
              </div>
            )}
            <Button onClick={saveIdentityFields} disabled={savingIdentity || (!idAddress.trim() && !idNationality.trim())}
              variant="outline" className="border-slate-300" data-testid="identity-save-btn">
              {savingIdentity ? "Saving..." : "Save details"}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="mb-6 bg-white border-slate-200" data-testid="agreement-preview-card">
        <CardHeader>
          <CardTitle className="text-slate-900">Your agreement document</CardTitle>
          <CardDescription className="text-slate-600">
            This is the actual document that will be signed and delivered to the
            partner — pre-filled with your verified details. Enter your amount
            below and refresh to see it on the document.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {previewUrl ? (
            <iframe src={previewUrl} title="Agreement preview"
              className="w-full h-[480px] border border-slate-200 rounded-md"
              data-testid="agreement-preview-frame" />
          ) : (
            <div className="h-32 bg-slate-100 rounded animate-pulse" />
          )}
          <Button variant="outline" className="mt-3 border-slate-300"
            disabled={previewLoading}
            onClick={() => loadPreview(amount)}
            data-testid="agreement-preview-refresh">
            {previewLoading ? "Refreshing..." : "Refresh preview with my amount"}
          </Button>
        </CardContent>
      </Card>

      <SignForm data={data} amount={amount} setAmount={setAmount} typedName={typedName}
        setTypedName={setTypedName} acknowledged={acknowledged} setAcknowledged={setAcknowledged}
        submitting={submitting} sign={sign}
        fundingMethod={fundingMethod} setFundingMethod={setFundingMethod}
        fundingAsset={fundingAsset} setFundingAsset={setFundingAsset} />
    </DashboardLayout>
  );
};

const ASSET_OPTIONS = [
  { value: "usdt_trc20", label: "USDT — TRC-20 (Tron)" },
  { value: "usdt_erc20", label: "USDT — ERC-20 (Ethereum)" },
  { value: "usdc_erc20", label: "USDC — ERC-20 (Ethereum)" },
];

const SignForm = ({ data, amount, setAmount, typedName, setTypedName, acknowledged, setAcknowledged, submitting, sign, fundingMethod, setFundingMethod, fundingAsset, setFundingAsset }) => (
  <Card className="bg-white border-slate-200" data-testid="agreement-sign-card">
    <CardHeader>
      <CardTitle className="text-slate-900">Elect your participation and sign</CardTitle>
      <CardDescription className="text-slate-600">{data.minimum_language}</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div>
        <Label className="text-slate-900">Participation amount (USD)</Label>
        <Input
          type="number"
          min={data.minimum_amount}
          step="1000"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={`${data.minimum_amount.toLocaleString()} minimum`}
          className="mt-1 bg-white text-slate-900 border-slate-300"
          data-testid="agreement-amount"
        />
      </div>
      <div>
        <Label className="text-slate-900">How do you intend to fund? (optional — you can change this later)</Label>
        <div className="mt-1 flex flex-wrap gap-4 text-sm text-slate-700" data-testid="funding-method-election">
          <label className="flex items-center gap-2 select-none">
            <input type="radio" name="funding-method" className="accent-slate-900"
              checked={fundingMethod === "wire"} onChange={() => setFundingMethod("wire")} />
            Bank wire (SSB)
          </label>
          <label className="flex items-center gap-2 select-none">
            <input type="radio" name="funding-method" className="accent-slate-900"
              checked={fundingMethod === "crypto"} onChange={() => setFundingMethod("crypto")} />
            Crypto
          </label>
          {fundingMethod && (
            <button type="button" className="text-xs text-slate-500 underline"
              onClick={() => { setFundingMethod(""); setFundingAsset(""); }}>
              clear
            </button>
          )}
        </div>
        {fundingMethod === "crypto" && (
          <select
            value={fundingAsset}
            onChange={(e) => setFundingAsset(e.target.value)}
            className="mt-2 w-full max-w-xs rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
            data-testid="funding-asset-select"
          >
            <option value="">Choose the asset & network…</option>
            {ASSET_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
      </div>
      <div>
        <Label className="text-slate-900">Type your full legal name to sign</Label>
        <Input
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder={data.identity.full_name}
          className="mt-1 bg-white text-slate-900 border-slate-300"
          data-testid="agreement-typed-name"
        />
      </div>
      <label className="flex items-start gap-2 text-sm text-slate-700 select-none" data-testid="agreement-acknowledge">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="h-4 w-4 mt-0.5 accent-slate-900"
        />
        <span>
          I have read and agree to the full agreement above, including the risk
          disclosures. I understand this constitutes my electronic signature.
        </span>
      </label>
      <Button
        onClick={sign}
        disabled={submitting || !acknowledged || !amount || !typedName}
        className="bg-slate-900 text-white hover:bg-slate-800"
        data-testid="agreement-sign-btn"
      >
        {submitting ? "Signing..." : "Sign agreement"}
      </Button>
      <p className="text-xs text-slate-500">
        Do not send funds until you have received a fully executed agreement
        and official FinBPM funding instructions.
      </p>
    </CardContent>
  </Card>
);

export default PartnerAgreementPage;
